import { inlineAtomNode } from '#src/tiptap/nodes/shared.ts'

export const ObservationNode = inlineAtomNode('observation', 'a', {
  reference: { dataAttr: 'data-reference' },
  display: { isDisplay: true, default: '' },
  codingDisplay: { dataAttr: 'data-coding-display', default: '' },
  system: { dataAttr: 'data-system', default: '' },
  code: { dataAttr: 'data-code', default: '' },
  scaleType: { dataAttr: 'data-scale-type' },
  unit: { dataAttr: 'data-unit' },
  /* The coded answer list an ordinal observation is scored against (the
   * Pain-severity LOINC answers, for one). An observation with no answer list
   * still renders `data-answer-options="[]"`, because `[]` is truthy — see the
   * round-trip test asserting exactly that. */
  answerOptions: { dataAttr: 'data-answer-options', default: [], json: true },
  /* The value set answers are searched in when the list is too long to carry
   * in `answerOptions`. */
  answerValueSet: { dataAttr: 'data-answer-value-set' },
  // The resource type a `Ref` observation's answer must point at.
  referenceResourceType: { dataAttr: 'data-reference-resource-type' },
  // `date` or `dateTime` on a `Qn` code answered with a point in time.
  valueKind: { dataAttr: 'data-value-kind' },
  /* A choice that records every selected answer, one Observation each. */
  repeats: { dataAttr: 'data-repeats', boolean: true },
  /* Those answers: `{ reference, code }`, omitted until one is recorded. */
  references: { dataAttr: 'data-references', default: null, json: true },
  /* The grouping key for those answers, shared by every Observation the
   * node records. Omitted until the first answer. */
  groupId: { dataAttr: 'data-group-id' },
  /* "latest": the node shows the participant's latest recorded value for its
   * code rather than capturing one. Absent (the default) means it captures,
   * so notes written before this attribute existed render unchanged. */
  mode: { dataAttr: 'data-mode' },
  /* What a frozen node recorded before it was frozen, to go back to. Kept
   * out of `data-reference`/`data-references`, so nothing that reads those
   * (signing, mentions, removal, value hydration) acts on a frozen node. */
  frozenReference: { dataAttr: 'data-frozen-reference' },
  frozenReferences: {
    dataAttr: 'data-frozen-references',
    default: null,
    json: true,
  },
  /* The node's own identity in the note, for conditions to point at and
   * settle on; given on insert and on paste when missing or repeated. */
  id: { dataAttr: 'data-id' },
  // A name authors refer to it by in conditions (`#fh_cancer`): a slug.
  name: { dataAttr: 'data-name' },
  // The condition under which the node shows, as a JSON Condition.
  showWhen: { dataAttr: 'data-show-when', default: null, json: true },
  // The calculation chosen for a computed code (BMI, BSA); absent means the
  // code's first rule.
  derivedRuleId: { dataAttr: 'data-derived-rule-id' },
  // The published cutoffs chosen to band a computed value (BMI for Asian
  // American or WHO adults); absent means the code's default set.
  derivedBandsId: { dataAttr: 'data-derived-bands-id' },
  // A computed value changed by hand, which recalculation leaves alone until
  // asked.
  derivedOverride: { dataAttr: 'data-derived-override', boolean: true },
  /* The answer of a note whose records are made when it is signed or
   * submitted: the Observation as it will be written, without an id or
   * subject. Held in the note until then, and kept after, so the note shows
   * what was signed. */
  draft: { dataAttr: 'data-draft', default: null, json: true },
  // A repeating choice's answers, one draft each.
  drafts: { dataAttr: 'data-drafts', default: null, json: true },
  /* A template's mark that the note cannot be signed or submitted without
   * an answer here, while the node shows. */
  required: { dataAttr: 'data-required', boolean: true },
})
